import anchorPoints from './anchorPoints'

export default getCoordinates

/**
 * @typedef {Object} Payload
 * @property {string} anchorPoint
 * @property {DOMRect} actionRect
 * @property {DOMRect} panelRect
 * @property {DOMRect} viewportRect
 */

/**
 * @typedef {Object} Position
 * @property {number} top
 * @property {number} left
 */

/**
 * @typedef {Object} Coordinates
 * @property {string} anchorPoint
 * @property {Position} containerPosition
 */

const {
  TOP_LEFT,
  TOP,
  TOP_RIGHT,
  RIGHT_TOP,
  RIGHT,
  RIGHT_BOTTOM,
  BOTTOM_LEFT,
  BOTTOM,
  BOTTOM_RIGHT,
  LEFT_TOP,
  LEFT,
  LEFT_BOTTOM
} = anchorPoints

/**
 * @type {Position}
 */
const DEFAULT_POSITION = Object.freeze({
  top: 0,
  left: 0
})

/**
 * @type {Coordinates}
 */
const DEFAULT_COORDINATES = Object.freeze({
  anchorPoint: RIGHT,
  containerPosition: DEFAULT_POSITION
})

/**
 * @param {Payload} payload
 * @returns {number}
 */
function calculateTopOffset ({ anchorPoint, actionRect, panelRect }) {
  switch (anchorPoint) {
    case BOTTOM_LEFT:
    case BOTTOM:
    case BOTTOM_RIGHT:
      return actionRect.height
    case TOP_LEFT:
    case TOP:
    case TOP_RIGHT:
      return -1 * panelRect.height
    case LEFT_TOP:
    case RIGHT_TOP:
      return 0
    case LEFT:
    case RIGHT:
      return (panelRect.height - actionRect.height) / -2
    case LEFT_BOTTOM:
    case RIGHT_BOTTOM:
      return -1 * (panelRect.height - actionRect.height)
    default:
      return DEFAULT_COORDINATES.containerPosition.top
  }
}

/**
 * @param {Payload} payload
 * @returns {number}
 */
function calculateLeftOffset ({ anchorPoint, actionRect, panelRect }) {
  switch (anchorPoint) {
    case RIGHT_TOP:
    case RIGHT:
    case RIGHT_BOTTOM:
      return actionRect.width
    case LEFT_TOP:
    case LEFT:
    case LEFT_BOTTOM:
      return -1 * panelRect.width
    case TOP_LEFT:
    case BOTTOM_LEFT:
      return 0
    case TOP:
    case BOTTOM:
      return -1 * ((panelRect.width - actionRect.width) / 2)
    case TOP_RIGHT:
    case BOTTOM_RIGHT:
      return -1 * (panelRect.width - actionRect.width)
    default:
      return DEFAULT_COORDINATES.containerPosition.top
  }
}

/**
 * @param {Payload} payload
 * @returns {Coordinates}
 */
function calculatePosition (props) {
  return {
    top: calculateTopOffset(props),
    left: calculateLeftOffset(props)
  }
}

/**
 * @param {Payload} payload
 * @returns {Coordinates}
 */
function calculateCoordinates (props) {
  return {
    anchorPoint: props.anchorPoint,
    containerPosition: calculatePosition(props)
  }
}

/**
 * Determines whether the given position is entirely within the viewport
 * @param {Payload} payload
 * @returns {function(Coordinates): boolean}
 */
function createViewportDeterminer (props) {
  const { viewportRect, panelRect, actionRect } = props

  return function isInViewport ({ containerPosition }) {
    const containerTop = actionRect.top + containerPosition.top
    const containerLeft = actionRect.left + containerPosition.left
    const containerRight = containerLeft + panelRect.width
    const containerBottom = containerTop + panelRect.height

    return (
      containerLeft >= viewportRect.left &&
      containerTop >= viewportRect.top &&
      containerRight <= viewportRect.right &&
      containerBottom <= viewportRect.bottom
    )
  }
}

/**
 * Go through the fallback anchor points, and returns the first that is within the viewport.
 * @param {Payload} payload
 * @returns {Coordinates}
 */
function getFallbackCoordinates (payload) {
  const isInViewport = createViewportDeterminer(payload)
  const { fallbackAnchorPoints } = payload

  for (let i = 0; i < fallbackAnchorPoints.length; i++) {
    const coordinates = calculateCoordinates(
      Object.assign({}, payload, { anchorPoint: fallbackAnchorPoints[i] })
    )
    if (isInViewport(coordinates)) {
      return coordinates
    }
  }

  return calculateCoordinates(payload)
}

/**
 * @param {Payload} payload
 * @returns {Coordinates}
 */
function getCoordinates (payload) {
  const coordinates = calculateCoordinates(payload)
  const isInViewport = createViewportDeterminer(payload)
  if (isInViewport(coordinates)) {
    return coordinates
  }

  return getFallbackCoordinates(payload)
}
